import { CopyIconButton, Tag, Text, Tooltip } from "@aurem/ui";
import { MessageSquare } from "lucide-react";
import {
  gql,
  InternalChatThreadOrderByColumn,
  InternalChatThreadsFilterField,
  MessageFromEnum,
} from "types";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

type ChatMessage = {
  id: string;
  from: MessageFromEnum;
  content: string;
  status: string;
  createdAt: string;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "FAILED":
      return "danger";
    case "CANCELED":
      return "warning";
    case "PROCESSING":
      return "primary";
    default:
      return "neutral";
  }
};

const renderMessages = (value: unknown) => {
  const messages = value as ChatMessage[] | null | undefined;
  if (!messages || messages.length === 0) return "-";

  const count = messages.length;

  return (
    <Tooltip
      content={
        <div className="max-h-96 overflow-auto space-y-2 p-1">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className="border-b border-neutral-200 pb-2 last:border-0"
            >
              <div className="flex items-center gap-1 mb-1">
                <Tag
                  size="sm"
                  color={msg.from === "USER" ? "primary" : "neutral"}
                  label={msg.from}
                />
                <Tag
                  size="sm"
                  color={getStatusColor(msg.status)}
                  label={msg.status}
                />
              </div>
              <Text size="xs" className="line-clamp-2">
                {msg.content}
              </Text>
            </div>
          ))}
        </div>
      }
    >
      <div className="inline-flex items-center gap-1 group cursor-help">
        <Tag color="neutral" label={`${count} messages`} />
        <CopyIconButton
          copyContent={JSON.stringify(messages, null, 2)}
          size="xs"
          variant="ghost"
          className="opacity-0 group-hover:opacity-100"
        />
      </div>
    </Tooltip>
  );
};

const GET_CHAT_THREAD_LIST = gql(/* GraphQL */ `
  query InternalChatThreadList(
    $pagination: Pagination
    $filters: InternalChatThreadsFiltersExpression
    $orderBy: InternalChatThreadOrderByInput
  ) {
    internalChatThreadList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        title
        description
        isStarred
        userId
        user {
          id
          profile {
            name
          }
        }
        messages {
          id
          from
          content
          status
          createdAt
        }
        createdAt
        lastMessageAt
      }
    }
  }
`);

export const chatThreadResourceConfig: InternalResourceConfig = {
  resourceName: "chat-thread",
  displayNameKey: "apps.admin.internalResources.chatThread.title",
  basePath: "/internal/data/chat-thread",

  listQuery: GET_CHAT_THREAD_LIST,
  listQueryName: "internalChatThreadList",

  fields: [
    {
      key: InternalChatThreadsFilterField.Title,
      labelKey: "apps.admin.internalResources.chatThread.columns.title",
      type: FieldType.String,
      sortKey: InternalChatThreadOrderByColumn.Title,
      width: 200,
    },
    {
      key: "description",
      labelKey: "apps.admin.internalResources.chatThread.columns.description",
      type: FieldType.String,
      width: 200,
    },
    {
      key: "isStarred",
      labelKey: "apps.admin.internalResources.chatThread.columns.starred",
      type: FieldType.Boolean,
      width: 80,
    },
    {
      key: "messages",
      labelKey: "apps.admin.internalResources.chatThread.columns.messages",
      type: FieldType.String,
      width: { min: 200, grow: 1 },
      format: renderMessages,
    },
    {
      key: "userId",
      labelKey: "apps.admin.internalResources.chatThread.columns.userId",
      type: FieldType.User,
      width: 180,
    },
    {
      key: "createdAt",
      labelKey: "apps.admin.internalResources.chatThread.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalChatThreadOrderByColumn.CreatedAt,
      width: 160,
    },
    {
      key: "lastMessageAt",
      labelKey: "apps.admin.internalResources.chatThread.columns.lastMessageAt",
      type: FieldType.DateTime,
      sortKey: InternalChatThreadOrderByColumn.LastMessageAt,
      width: 160,
    },
  ],

  list: {
    defaultSort: {
      column: InternalChatThreadOrderByColumn.CreatedAt,
      direction: "desc",
    },
    searchFields: [InternalChatThreadsFilterField.Title],
  },

  navigation: {
    icon: <MessageSquare size={16} />,
    order: 4,
  },
};
